import {reactive} from 'vue';
import {englishMessage, type MessageKey} from '/src/i18n/messages';

export type AuthEmailPasswordMode =
    'sign-in' | 'register' | 'register-sent' | 'forgot-password' | 'forgot-password-sent' | 'reset-password';

type AuthEmailPasswordResponse = {
    result?: {
        session?: unknown | null;
        user?: {
            identities?: unknown[];
        } | null;
    } | null;
    error: {
        message: string;
        code?: string;
    } | null;
};

type AuthEmailPasswordDependencies = {
    signInWithPassword: (email: string, password: string) => Promise<AuthEmailPasswordResponse>;
    signUpWithPassword: (email: string, password: string) => Promise<AuthEmailPasswordResponse>;
    sendPasswordReset: (email: string) => Promise<AuthEmailPasswordResponse>;
    updatePassword: (password: string) => Promise<AuthEmailPasswordResponse>;
    onPasswordRecovery: (callback: () => void) => () => void;
    navigateToAuthenticatedApp: () => Promise<void> | void;
    logEvent: (eventName: string, option?: string | number) => void;
};

type AuthEmailPasswordOptions = {
    initialMode?: AuthEmailPasswordMode;
};

export type AuthEmailPasswordState = {
    mode: AuthEmailPasswordMode;
    email: string;
    password: string;
    confirmPassword: string;
    isLoading: boolean;
    errorMessage: AuthMessage | null;
    successMessage: AuthMessage | null;
};

/** A dictionary message, or the server's own text when the error has no translation. */
export type AuthMessage = {key: MessageKey; values?: Record<string, string>} | {text: string};

export type AuthEmailPasswordContent = {
    title: MessageKey;
    submitLabel: MessageKey;
};

/** Supabase Auth errors by code; codes without a translation keep the server's English text. */
export function getAuthErrorMessage(error: {message: string; code?: string}): AuthMessage {
    const key = `auth.errors.${error.code}`;
    return error.code && englishMessage(key) !== undefined ? {key: key as MessageKey} : {text: error.message};
}

export function isAuthEmailPasswordRecoveryMode(search: string, hash: string = '') {
    const searchParams = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

    return (
        searchParams.get('mode') === 'recovery' ||
        searchParams.get('type') === 'recovery' ||
        hashParams.get('type') === 'recovery'
    );
}

export type AuthEmailPasswordRecoveryError = {
    code: string;
    description: string;
};

export function getAuthEmailPasswordRecoveryError(
    search: string,
    hash: string = '',
): AuthEmailPasswordRecoveryError | null {
    const searchParams = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);

    const error = searchParams.get('error') ?? hashParams.get('error');
    const code = searchParams.get('error_code') ?? hashParams.get('error_code');
    const description = searchParams.get('error_description') ?? hashParams.get('error_description');

    if (!error) {
        return null;
    }

    return {
        code: code ?? error,
        description: description ?? 'This password reset link is invalid or has expired.',
    };
}

export function getAuthEmailPasswordContent(mode: AuthEmailPasswordMode): AuthEmailPasswordContent {
    if (mode === 'forgot-password') {
        return {title: 'auth.modes.forgotPassword.title', submitLabel: 'auth.modes.forgotPassword.submit'};
    }

    if (mode === 'reset-password') {
        return {title: 'auth.modes.resetPassword.title', submitLabel: 'auth.modes.resetPassword.submit'};
    }

    if (mode === 'register') {
        return {title: 'auth.modes.register.title', submitLabel: 'auth.modes.register.submit'};
    }

    return {title: 'auth.modes.signIn.title', submitLabel: 'auth.modes.signIn.submit'};
}

export type AuthEmailPasswordAutocomplete = {
    email: string;
    password: string;
};

/**
 * Autocomplete tokens that let the browser password manager recognize the account fields and
 * offer to save them: "current-password" for an existing credential, "new-password" for a created one.
 */
export function getAuthEmailPasswordAutocomplete(mode: AuthEmailPasswordMode): AuthEmailPasswordAutocomplete {
    return {
        email: 'username',
        password: mode === 'sign-in' ? 'current-password' : 'new-password',
    };
}

export function createAuthEmailPassword(
    dependencies: AuthEmailPasswordDependencies,
    options: AuthEmailPasswordOptions = {},
) {
    const state = reactive<AuthEmailPasswordState>({
        mode: options.initialMode ?? 'sign-in',
        email: '',
        password: '',
        confirmPassword: '',
        isLoading: false,
        errorMessage: null,
        successMessage: null,
    });

    let unsubscribePasswordRecovery = () => {};

    function clearFeedback() {
        state.errorMessage = null;
        state.successMessage = null;
    }

    function showSignIn() {
        state.mode = 'sign-in';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function showRegister() {
        state.mode = 'register';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function showForgotPassword() {
        state.mode = 'forgot-password';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function enterRecoveryMode() {
        state.mode = 'reset-password';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    function goBackToSignIn() {
        state.mode = 'sign-in';
        state.password = '';
        state.confirmPassword = '';
        clearFeedback();
    }

    async function submit() {
        if (state.isLoading) {
            return;
        }

        clearFeedback();
        state.isLoading = true;

        try {
            const email = state.email.trim();

            if (state.mode === 'sign-in') {
                if (!email || !state.password) {
                    state.errorMessage = {key: 'auth.validation.signInMissing'};
                    dependencies.logEvent('email_sign_in_failure', 'missing_credentials');
                    return;
                }

                const {error} = await dependencies.signInWithPassword(email, state.password);
                if (error) {
                    state.errorMessage = getAuthErrorMessage(error);
                    dependencies.logEvent('email_sign_in_failure', 'invalid_credentials');
                    return;
                }

                dependencies.logEvent('email_sign_in_success');
                state.successMessage = {key: 'auth.messages.signingIn'};
                await dependencies.navigateToAuthenticatedApp();
                return;
            }

            if (state.mode === 'register') {
                if (!email || !state.password) {
                    state.errorMessage = {key: 'auth.validation.registerMissing'};
                    return;
                }

                const {result, error} = await dependencies.signUpWithPassword(email, state.password);
                if (error) {
                    state.errorMessage = getAuthErrorMessage(error);
                    return;
                }

                if (result?.user?.identities?.length === 0) {
                    state.errorMessage = {key: 'auth.validation.accountExists'};
                    return;
                }

                if (result?.session) {
                    dependencies.logEvent('email_register_success', 'session_created');
                    await dependencies.navigateToAuthenticatedApp();
                    return;
                }

                dependencies.logEvent('email_register_success', 'confirmation_required');
                state.mode = 'register-sent';
                return;
            }

            if (state.mode === 'forgot-password') {
                if (!email) {
                    state.errorMessage = {key: 'auth.validation.resetEmailMissing'};
                    return;
                }

                const {error} = await dependencies.sendPasswordReset(email);
                if (error) {
                    state.errorMessage = getAuthErrorMessage(error);
                    return;
                }

                dependencies.logEvent('password_reset_requested');
                state.mode = 'forgot-password-sent';
                state.successMessage = {key: 'auth.messages.resetLinkSent', values: {email}};
                return;
            }

            if (!state.password) {
                state.errorMessage = {key: 'auth.validation.newPasswordMissing'};
                return;
            }

            if (state.password !== state.confirmPassword) {
                state.errorMessage = {key: 'auth.validation.passwordsDiffer'};
                return;
            }

            const {error} = await dependencies.updatePassword(state.password);
            if (error) {
                state.errorMessage = getAuthErrorMessage(error);
                return;
            }

            dependencies.logEvent('password_reset_completed');
            state.successMessage = {key: 'auth.messages.passwordUpdated'};
            await dependencies.navigateToAuthenticatedApp();
        } finally {
            state.isLoading = false;
        }
    }

    function initialize() {
        unsubscribePasswordRecovery = dependencies.onPasswordRecovery(() => {
            enterRecoveryMode();
        });
    }

    function dispose() {
        unsubscribePasswordRecovery();
    }

    return {
        state,
        initialize,
        dispose,
        submit,
        goBackToSignIn,
        showSignIn,
        showRegister,
        showForgotPassword,
        enterRecoveryMode,
    };
}
